import {AxiosError} from 'axios';

import {
    BankDTO,
    BankVerificationDTO,
    BankVerificationErrorDTO,
    Completable,
    completed,
    KashableResponseDTO,
    PlaidTokenDTO,
} from 'kashable-mobile-common';

import {errored, mapSucceeded} from '../utils/AxiosUtils';

import axios from './base';

export interface AddBankValues {
    aba_number: string;
    account_number: string;
    account_type: BankDTO.AccountTypeEnum;
    token: string;
}

export enum PlaidVerificationType {
    QUEUE,
    CONFIRM_PRIMARY
}

const getLinkToken: (verifyPrimary: boolean) => Promise<Completable<PlaidTokenDTO>> = (verifyPrimary: boolean) => {
    return axios
        .post<KashableResponseDTO<PlaidTokenDTO>>('/secured/user/bank/init', {verify_primary: verifyPrimary})
        .then((res) => mapSucceeded(res, (d) => d.data))
        .catch((error: AxiosError<string>) => errored(error));
};

const verifyBank = (dto: BankVerificationDTO, type: PlaidVerificationType): Promise<Completable<boolean>> => {

    const url = (() => {switch (type) {
        case PlaidVerificationType.QUEUE:
            return '/secured/user/bank/verify';
        case PlaidVerificationType.CONFIRM_PRIMARY:
            return '/secured/user/bank/verify/primary';
    }})();

    return axios
        .post<KashableResponseDTO<boolean>>(url, dto)
        .then((res) => completed(res.data.data))
        .catch((error: AxiosError<string>) => errored(error));
};

const verifyBankError = (dto: BankVerificationErrorDTO, type: PlaidVerificationType): Promise<Completable<boolean>> => {

    const url = (() => {switch (type) {
        case PlaidVerificationType.QUEUE:
            return '/secured/user/bank/verify/error';
        case PlaidVerificationType.CONFIRM_PRIMARY:
            return '/secured/user/bank/verify/primary/error';
    }})();

    return axios
        .post<KashableResponseDTO<boolean>>(url, dto)
        .then((res) => completed(res.data.data))
        .catch((error: AxiosError<string>) => errored(error));
};

const fetchPrimaryBank: () => Promise<Completable<BankDTO>> = () => {
    return axios
        .get<KashableResponseDTO<BankDTO>>('/secured/user/bank/primary')
        .then((res) => mapSucceeded(res, (d) => d.data))
        .catch((error: AxiosError<string>) => errored(error));
};

const fetchBackupBank: () => Promise<Completable<BankDTO>> = () => {
    return axios
        .get<KashableResponseDTO<BankDTO>>('/secured/user/bank/backup')
        .then((res) => mapSucceeded(res, (d) => d.data))
        .catch((error: AxiosError<string>) => errored(error));
};

const getValidatedBankList: (ach?: boolean) => Promise<Completable<Array<BankDTO>>> = (ach?: boolean) => {
    const config = ach ? '1' : '0';
    return axios
        .get<KashableResponseDTO<Array<BankDTO>>>(`/secured/user/bank?ach=${config}`)
        .then((res) => mapSucceeded(res, (d) => d.data))
        .catch((error: AxiosError<string>) => errored(error));
};

const addNewBank = (dto: BankDTO): Promise<Completable<boolean>> => {
    return axios
        .post<KashableResponseDTO<boolean>>('/secured/user/bank', dto)
        .then((res) => mapSucceeded(res, (d) => d.data))
        .catch((error: AxiosError<string>) => errored(error));
};

const addNewBankAch = (dto: BankDTO): Promise<Completable<boolean>> => {
    return axios
        .post<KashableResponseDTO<boolean>>('/secured/user/bank?m=ACH', dto)
        .then((res) => mapSucceeded(res, (d) => d.data))
        .catch((error: AxiosError<string>) => errored(error));
};

const setPrimaryBank = (dto: BankDTO): Promise<Completable<boolean>> => {
    return axios
        .put<KashableResponseDTO<boolean>>('/secured/user/bank', dto)
        .then((res) => mapSucceeded(res, (d) => d.data))
        .catch((error: AxiosError<string>) => errored(error));
};

const setAchBank = (bank: BankDTO): Promise<Completable<boolean>> => {
    return axios
        .put<KashableResponseDTO<boolean>>('/secured/user/bank/ach', bank)
        .then((res) => mapSucceeded(res, (d) => d.data))
        .catch((error: AxiosError<string>) => errored(error));
};

const setBackupBank = (bank: BankDTO): Promise<Completable<boolean>> => {
    return axios
        .put<KashableResponseDTO<boolean>>('/secured/user/bank/backup', bank)
        .then((res) => mapSucceeded(res, (d) => d.data))
        .catch((error: AxiosError<string>) => errored(error));
};

const confirmBank = (bank: BankDTO): Promise<Completable<boolean>> => {
    return axios
        .put<KashableResponseDTO<boolean>>('/secured/user/bank/confirm', bank)
        .then((res) => mapSucceeded(res, (d) => d.data))
        .catch((error: AxiosError<string>) => errored(error));
};

const unsecuredAddBank: (f: AddBankValues, r?: boolean) => Promise<Completable<boolean>> = (form: AddBankValues, refund?: boolean) => {
    const url = refund ? '/pub/refund/bank' : '/pub/bank';

    return axios
        .post<KashableResponseDTO<void>>(url, form)
        .then((res) => mapSucceeded(res, () => true))
        .catch((error: AxiosError<string>) => errored(error));
};

const unsecuredBankInfo: (k: string) => Promise<Completable<BankDTO>> = (key: string) => {
    return axios
        .get<KashableResponseDTO<BankDTO>>('/pub/bank/ach', {
            params: {key},
        })
        .then((res) => mapSucceeded(res, (d) => d.data))
        .catch((error: AxiosError<string>) => errored(error));
};

export {
    getLinkToken,
    verifyBank,
    verifyBankError,
    fetchPrimaryBank,
    getValidatedBankList,
    addNewBank,
    addNewBankAch,
    setPrimaryBank,
    setAchBank,
    unsecuredAddBank,
    unsecuredBankInfo,
    confirmBank,
    fetchBackupBank,
    setBackupBank
};
